import React, { useEffect, useState } from "react";
import { ArrowLeft, Save, Send, Loader2, AlertCircle, CheckCircle2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import FormField from "../components/FormField";
import StatusBadge from "../components/StatusBadge";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const STATUS_LABEL = { not_started: "Not Started", in_progress: "In Progress", submitted: "Submitted", approved: "Approved", rejected: "Rejected", returned: "Returned for Corrections" };

export default function FormFiller({ formId, onClose }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/forms/${formId}`).then((r) => {
    setData(r.data);
    setAnswers(r.data.submission?.answers || {});
    setFiles(r.data.submission?.files || {});
  }).catch((e) => toast({ title: "Couldn't load form", description: e.response?.data?.detail || "", variant: "destructive" }));
  useEffect(() => { load(); }, [formId]);

  if (!data) return <div className="py-10 text-center text-sm text-slate-400">Loading…</div>;

  const { form } = data;
  const status = data.submission?.status || "not_started";
  const editable = data.editable;

  const sigValue = () => {
    const sf = form.fields.find((f) => f.type === "signature");
    const name = sf ? answers[sf.id] : "";
    return { legal_name: name || "", signature: name || "" };
  };

  const doSave = async () => {
    setBusy(true);
    try {
      await api.post(`/forms/${formId}/save`, { answers, files, signature: sigValue() });
      toast({ title: "Progress saved" });
      load();
    } catch (e) { toast({ title: "Save failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      await api.post(`/forms/${formId}/submit`, { answers, files, signature: sigValue() });
      toast({ title: "Form submitted", description: "It's now with the review team." });
      onClose();
    } catch (e) { toast({ title: "Cannot submit", description: e.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="form-filler">
      <div className="flex items-center justify-between">
        <button onClick={onClose} data-testid="form-back-btn" className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-violet-600"><ArrowLeft className="h-4 w-4" /> Back to Forms</button>
        <StatusBadge status={status === "approved" ? "Approved" : status === "rejected" ? "Rejected" : status === "submitted" ? "Pending" : "In progress"} />
      </div>

      <Card>
        <h2 className="text-base font-bold text-slate-900">{form.title}</h2>
        {form.description && <p className="text-sm text-slate-500">{form.description}</p>}
        <span className="mt-2 inline-block text-xs font-semibold uppercase text-slate-400">{STATUS_LABEL[status]}</span>
      </Card>

      {data.submission?.review_comment && (status === "returned" || status === "rejected") && (
        <Card className="border-amber-200 bg-amber-50/60">
          <div className="flex items-start gap-2 text-sm text-amber-800"><AlertCircle className="mt-0.5 h-4 w-4" /><div><strong>Reviewer note:</strong> {data.submission.review_comment}</div></div>
        </Card>
      )}

      {!editable && (
        <Card className="border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 text-sm text-slate-600"><Lock className="h-4 w-4" /> This form is {status === "approved" ? "approved and" : ""} locked. It's read-only until a reviewer returns it for corrections.</div>
        </Card>
      )}

      <Card>
        <div className="space-y-5">
          {form.fields.map((field) => (
            <FormField key={field.id} field={field} formId={formId} readOnly={!editable}
              value={answers[field.id]} onChange={(v) => setAnswers((a) => ({ ...a, [field.id]: v }))}
              fileMeta={files[field.id]} onFile={(meta) => setFiles((f) => ({ ...f, [field.id]: meta }))} />
          ))}
        </div>
        {editable && (
          <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
            <Button variant="outline" onClick={doSave} disabled={busy} data-testid="form-save-btn" className="gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Progress</Button>
            <Button onClick={doSubmit} disabled={busy} data-testid="form-submit-btn" className="btn-primary-gradient gap-1.5 border-0 text-white"><Send className="h-4 w-4" /> Submit</Button>
          </div>
        )}
        {status === "approved" && (
          <div className="mt-6 flex items-center gap-2 border-t border-slate-100 pt-4 text-sm font-medium text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Approved{data.submission?.signature?.signed_at ? ` · signed ${new Date(data.submission.signature.signed_at).toLocaleString()}` : ""}</div>
        )}
      </Card>
    </div>
  );
}
